import { useEffect, useState } from 'react';
import { claimInvite, lookupInvite, type InviteDetails } from '../api';
import { download, safeFileName } from '../components/Keys';
import type { PortalConfig } from '../config';
import { UnsupportedBrowserError, generateEd25519KeyPair } from '../ssh-key';

// Accepting an invitation: the one page in the portal that works with no session.
//
// It exists because a key-only partner cannot sign in at all -- their account is
// created with a temporary password that is generated and discarded -- so the
// self-service key page is unreachable to exactly the people it is for. The
// invite link is the way in. See lib/portal-api/invites.mjs for what the link is
// worth and why it expires.
//
// Everything here is one submit. The invite is single-use, so a page that posted
// the password and the key separately would spend the invite on the first call
// and fail the second; the form collects both and sends them together.
export function ClaimInvitePage({ config }: { config?: PortalConfig }) {
  // Read once, into state, rather than on every render. The fragment is cleared
  // from the address bar after a successful claim, and a re-render must not then
  // decide the link was incomplete.
  const [link] = useState(() => readLink());

  const [details, setDetails] = useState<InviteDetails>();
  const [lookupError, setLookupError] = useState<string>();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [keyMode, setKeyMode] = useState<'paste' | 'generate'>('paste');
  const [publicKey, setPublicKey] = useState('');
  const [generated, setGenerated] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{ passwordSet: boolean; keyAdded: boolean }>();

  useEffect(() => {
    if (!link) {
      setLookupError(
        'This link is incomplete. Copy the whole thing from the message you were sent — it is ' +
          'longer than it looks and some mail clients cut it short.'
      );
      return;
    }
    void lookupInvite(link.username, link.token)
      .then(setDetails)
      .catch((err: unknown) => setLookupError(err instanceof Error ? err.message : String(err)));
  }, [link]);

  // The keypair is made here and the private half downloaded, but nothing is
  // registered yet -- that happens on the single submit below. Which is why this
  // is a separate button from "Finish setup", unlike the add-key form elsewhere
  // in the portal where generating and registering are one click.
  const generate = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const comment = link ? `${link.username}@sftp` : 'sftp';
      const pair = await generateEd25519KeyPair(comment);
      // Downloaded before it is held for submission, for the same reason as in
      // the add-key form: a blocked or cancelled download must not leave a key
      // registered whose private half is already gone.
      download(`${safeFileName(comment)}_ed25519`, pair.privateKey);
      download(`${safeFileName(comment)}_ed25519.pub`, `${pair.publicKey}\n`);
      setPublicKey(pair.publicKey);
      setGenerated(pair.fingerprint);
    } catch (err) {
      setError(
        err instanceof UnsupportedBrowserError || err instanceof Error ? err.message : String(err)
      );
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!link || !details) return;
    if (details.needsPassword && password && password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const result = await claimInvite({
        username: link.username,
        token: link.token,
        password: password || undefined,
        publicKey: publicKey.trim() || undefined,
      });
      setDone(result);
      // The token is spent, and a spent credential has no reason to stay in
      // browser history or to be re-sent by a reload.
      window.history.replaceState({}, '', '/claim');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (lookupError) {
    return (
      <main className="centered">
        <div className="card">
          <h1>Invitation</h1>
          <p className="error">{lookupError}</p>
        </div>
      </main>
    );
  }

  if (!details) return <main className="centered">Checking your invitation…</main>;

  if (done) {
    return (
      <main className="centered">
        <div className="card">
          <h1>You are set up</h1>
          {done.keyAdded && <p>Your public key is registered.</p>}
          {done.passwordSet && <p>Your password is set.</p>}
          <p className="hint">
            Connect over SFTP to <code>{config?.sftpEndpoint ?? 'the address you were given'}</code>{' '}
            on port 22 as <strong>{details.username}</strong>
            {done.keyAdded && ', using the private key you just downloaded'}.
          </p>
          {done.passwordSet && (
            <p>
              <a href="/">Sign in to the portal</a>
            </p>
          )}
          {/* Said once, here, because this page cannot be reopened: the invite is
              spent and the link no longer works. */}
          <p className="hint">This link has now been used and will not open again.</p>
        </div>
      </main>
    );
  }

  const canSubmit = Boolean(password || publicKey.trim());

  return (
    <main className="centered">
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <h1>Set up your SFTP access</h1>
        <p>
          Your account <strong>{details.username}</strong> is ready. Choose your own credentials
          below — nobody here will ever see them.
        </p>

        {details.needsPassword && (
          <>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required={!details.needsKey}
              />
            </label>
            <label>
              Confirm password
              <input
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required={!details.needsKey}
              />
            </label>
            <p className="hint">
              At least 12 characters, with an upper-case letter, a lower-case letter, a number and a
              symbol.
            </p>
          </>
        )}

        {details.needsKey && (
          <>
            {/* The heading only appears when both are on offer, where "or" is the
                thing that needs saying. */}
            {details.needsPassword && <p className="or">and / or an SSH key</p>}
            <div className="segmented">
              <button
                type="button"
                className={keyMode === 'paste' ? 'active' : undefined}
                onClick={() => setKeyMode('paste')}
              >
                I have a key
              </button>
              <button
                type="button"
                className={keyMode === 'generate' ? 'active' : undefined}
                onClick={() => setKeyMode('generate')}
              >
                Make one for me
              </button>
            </div>

            {keyMode === 'paste' ? (
              <>
                <label>
                  Public key
                  <textarea
                    rows={3}
                    value={publicKey}
                    onChange={(event) => setPublicKey(event.target.value)}
                    placeholder="ssh-ed25519 AAAAC3Nza… you@your-host"
                    spellCheck={false}
                  />
                </label>
                <p className="hint">
                  The contents of your <code>.pub</code> file. Never paste a private key here — if
                  you do, treat it as compromised and generate a new one.
                </p>
              </>
            ) : (
              <>
                <p className="hint">
                  The keypair is created in this browser. The private key downloads to this device
                  and is never sent to us, so it cannot be recovered if you lose it.
                </p>
                <button type="button" onClick={() => void generate()} disabled={busy}>
                  {generated ? 'Generate a different one' : busy ? 'Generating…' : 'Generate and download'}
                </button>
                {generated && (
                  <div className="notice">
                    <p>
                      Two files downloaded: the private key and its <code>.pub</code>.
                    </p>
                    <p>
                      Fingerprint <code>{generated}</code>
                    </p>
                    <p className="hint">
                      On macOS or Linux, move the private key into <code>~/.ssh/</code> and run{' '}
                      <code>chmod 600</code> on it. Then finish below to register it.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy || !canSubmit}>
          {busy ? 'Saving…' : 'Finish setup'}
        </button>
        <p className="hint">
          This link can only be used once, and expires{' '}
          {new Date(details.expiresAt).toLocaleString()}.
        </p>
      </form>
    </main>
  );
}

// The username and token travel in the URL fragment, which browsers never send
// to a server -- so they stay out of CloudFront and API Gateway access logs. See
// inviteUrl() in api.ts.
const readLink = (): { username: string; token: string } | undefined => {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const username = params.get('u');
  const token = params.get('t');
  if (!username || !token) return undefined;
  return { username, token };
};
